import { ChangeEvent, Dispatch, SetStateAction, useEffect, useState } from "react";
import { Modal, Button, Select, MenuItem, CardMedia, Input, CircularProgress } from "@mui/material";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import SwitchToggle from "../components/SwitchToggle";
import { useGetBrandsQuery, useGetCategoriesQuery, useAddModelMutation, useUpdateModelMutation, useUploadMediaMutation } from "../service/features/api";
import { toast } from "react-toastify";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  modelData?: any;
}

export default function AddModel({
  open,
  onClose,
  setOpen,
  modelData
}: ModalProps) {

  const [selectedBrand, setSelectedBrand] = useState<string>("");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [name, setName] = useState<string>("");
  const [image, setImage] = useState<string>("");
  const [isPopular, setIsPopular] = useState<boolean>(false);
  const [fileName, setFileName] = useState<string>("");
  const [errors, setErrors] = useState<any>({});

  // Queries
  const { data: brandsData } = useGetBrandsQuery({ page: 1, limit: 100 });
  const { data: categoriesData } = useGetCategoriesQuery({ page: 1, limit: 100, brandId: selectedBrand }, { skip: !selectedBrand });
  const [addModel, { isLoading: isAdding }] = useAddModelMutation();
  const [updateModel, { isLoading: isUpdating }] = useUpdateModelMutation();
  const [uploadMedia, { isLoading: isUploading }] = useUploadMediaMutation();

  useEffect(() => {
    if (modelData && open) {
      setName(modelData.name || "");
      setSelectedBrand(modelData.brandId || modelData.brand?._id || "");

      let catIds: string[] = [];
      if (modelData.categoryId) {
        catIds = Array.isArray(modelData.categoryId) ? modelData.categoryId : [modelData.categoryId];
      } else if (modelData.category) {
        catIds = modelData.category.map((c: any) => c._id);
      }
      setSelectedCategories(catIds);
      setImage(modelData.image || modelData.logo || "");
      setIsPopular(modelData.isPopular || false);
      setErrors({});
    } else if (!modelData && open) {
      // Reset form
      setName("");
      setSelectedBrand("");
      setSelectedCategories([]);
      setImage("");
      setIsPopular(false);
      setErrors({});
      setFileName("");
    }
  }, [modelData, open]);

  const handleBrandChange = (event: any) => {
    setSelectedBrand(event.target.value as string);
    setSelectedCategories([]); // Reset categories when brand changes
    if (errors.brand) setErrors({ ...errors, brand: "" });
  };

  const handleCategoryChange = (event: any) => {
    const value = event.target.value as string[];
    setSelectedCategories(typeof value === 'string' ? (value as string).split(',') : value);
    if (errors.category) setErrors({ ...errors, category: "" });
  };

  const handleNameChange = (e: any) => {
    if (e.target.value.length <= 40) {
      setName(e.target.value);
      if (errors.name) setErrors({ ...errors, name: "" });
    }
  };

  const handleValidation = () => {
    let tempErrors: any = {};
    let isValid = true;

    if (!name.trim()) {
      tempErrors.name = "Model name is required";
      isValid = false;
    }
    if (!selectedBrand) {
      tempErrors.brand = "Brand is required";
      isValid = false;
    }
    if (selectedCategories.length === 0) {
      tempErrors.category = "At least one category is required";
      isValid = false;
    }
    if (!image) {
      tempErrors.image = "Image is required";
      isValid = false;
    }

    setErrors(tempErrors);
    return isValid;
  };

  const handleImageUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files[0]) {
      const file = files[0];
      if (!file.type.includes("image")) {
        toast.error("Please upload a valid image file");
        return;
      }

      setFileName(file.name);

      const formData = new FormData();
      formData.append("file", file);

      try {
        const res = await uploadMedia(formData).unwrap();
        const uploadedUrl = res?.data?.url || res?.url || res?.data;
        if (uploadedUrl) {
          setImage(uploadedUrl);
          if (errors.image) setErrors({ ...errors, image: "" });
          toast.success("Image uploaded successfully");
        }
      } catch (err: any) {
        toast.error("Failed to upload image");
      }
    }
  };

  const handleSubmit = async () => {
    if (!handleValidation()) return;

    const payload = {
      name,
      image,
      categoryId: selectedCategories,
      brandId: selectedBrand,
      isPopular
    };

    try {
      if (modelData?._id) {
        await updateModel({ id: modelData._id, ...payload }).unwrap();
        toast.success("Model updated successfully");
      } else {
        await addModel(payload).unwrap();
        toast.success("Model added successfully");
      }
      setOpen(false);
    } catch (err: any) {
      toast.error(err?.data?.message || "Something went wrong");
    }
  };

  return (
    <Modal
      className="modal rejectReason_modal"
      open={open}
      onClose={onClose}
    >
      <div className="modal-dialog">
        <div className="modal-body">
          <div className="modal_title">
            <h2>{modelData ? "تعديل موديل / Edit Model" : "إضافة موديل / Add Model"}</h2>
            <button
              type="button"
              className="btn-close"
              onClick={() => setOpen(false)}
              aria-label="Close"
            >
              <CloseIcon />
            </button>
          </div>

          <form className="form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
            <div className="gap_p">
              <div className="control_group w_100">
                <label htmlFor="upload-image">
                  تحميل الصورة / Upload Image<span className="req">*</span>
                </label>

                {isUploading ? (
                  <div className="upload_image no_radius" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <CircularProgress size={24} />
                  </div>
                ) : image ? (
                  <div className="upload_image no_radius">
                    <div className="upload_image_holder">
                      <figure>
                        <CardMedia component="img" image={image} alt="Category" />
                      </figure>
                      <CloseIcon
                        className="remove_img"
                        onClick={() => {
                          setImage("");
                          setFileName("");
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <label
                    className="upload_image no_radius"
                    htmlFor="upload-image"
                  >
                    <Input
                      sx={{ display: "none" }}
                      id="upload-image"
                      type="file"
                      inputProps={{ accept: "image/png,image/jpeg" }}
                      onChange={handleImageUpload}
                      disabled={isUploading}
                    />
                    <span className="upload_image_holder">
                      <figure className="add_img">
                        <img
                          src="/static/images/placeholder.png"
                          alt="Upload"
                        />
                        <span className="upload_img_icon">
                          <img
                            src="/static/images/edit2_icon.svg"
                            alt="Edit"
                          />
                        </span>
                      </figure>
                    </span>
                  </label>
                )}
                {errors.image && <span className="error_text" style={{ color: 'red', fontSize: '12px' }}>{errors.image}</span>}
              </div>

              <div className="control_group w_50">
                <label htmlFor="model-name">اسم الموديل / Model Name</label>
                <TextField
                  id="model-name"
                  placeholder="أدخل الاسم / Enter Name"
                  fullWidth
                  hiddenLabel
                  value={name}
                  onChange={handleNameChange}
                  error={!!errors.name}
                  helperText={errors.name}
                  inputProps={{ maxLength: 40 }}
                />
              </div>

              <div className="control_group w_50">
                <label>العلامة التجارية / Brand</label>
                <Select
                  fullWidth
                  hiddenLabel
                  value={selectedBrand}
                  onChange={handleBrandChange}
                  displayEmpty
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200,
                      },
                    },
                  }}
                  error={!!errors.brand}
                >
                  <MenuItem value="" disabled>حدد العلامة التجارية / Select Brand</MenuItem>
                  {brandsData?.data?.data?.map((brand: any) => (
                    <MenuItem key={brand._id} value={brand._id}>
                      {brand.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.brand && <span className="error_text" style={{ color: 'red', fontSize: '12px' }}>{errors.brand}</span>}
              </div>

              <div className="control_group">
                <label htmlFor="Category">الفئة / Category</label>
                <Select
                  multiple
                  fullWidth
                  hiddenLabel
                  value={selectedCategories}
                  onChange={handleCategoryChange}
                  renderValue={(selected) => {
                    const selectedIds = selected as string[];
                    if (selectedIds.length === 0) return "حدد الفئات / Select Categories";

                    return selectedIds.map(id =>
                      categoriesData?.data?.data?.find((cat: any) => cat._id === id)?.name
                    ).filter(Boolean).join(", ");
                  }}
                  displayEmpty
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200, // Limit height to enable scrolling
                      },
                    },
                  }}
                  error={!!errors.category}
                >
                  <MenuItem value="" disabled>حدد الفئات / Select Categories</MenuItem>
                  {categoriesData?.data?.data?.map((category: any) => (
                    <MenuItem key={category._id} value={category._id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.category && <span className="error_text" style={{ color: 'red', fontSize: '12px' }}>{errors.category}</span>}
              </div>

              {/* Popular */}
              <div className="control_group flex w_100">
                <label>هل هذا الموديل شائع؟ / Is this Popular Model?</label>
                <SwitchToggle
                  checked={isPopular}
                  onChange={(e) => setIsPopular(e.target.checked)}
                />
              </div>
            </div>

            <div className="footer_btn_flex">
              <Button
                type="submit"
                className="btn btn_primary"
                disabled={isAdding || isUpdating}
              >
                {isAdding || isUpdating ? <CircularProgress size={20} color="inherit" /> : "حفظ / Save"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}
